// MessageFeed — the scrollable middle section of the Decision Engine page.
// Owns the feed `<div>` and registers it via the context's `setFeedElement`
// callback ref so `scrollToBottom()` continues to work without exposing
// the underlying ref to siblings.
//
// Phase 3 Step 2d extraction. With this in place, the feed ref is fully
// encapsulated — only this component sets it, only `scrollToBottom()`
// reads it.
//
// The page keeps ownership of:
//   • `submit(query)`     — used by the quick-command buttons
//   • `resumeSession(id)` — used by RecentSessionsStrip's onResume
// …both passed in as callbacks.
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { AlertTriangle, Loader2, ListChecks, Sparkles } from 'lucide-react';

import { useDecisionEngine } from './state/DecisionEngineContext';
import { RiskBanner } from './RiskBanner';
import { RiskFactorsStrip } from './RiskFactorsStrip';
import { RiskSnapshotInline } from './RiskSnapshotInline';
import { ContextProvenanceBadge } from './ContextProvenanceBadge';
import { DecisionCard } from './DecisionCard';
import { RecentSessionsStrip } from './RecentSessionsStrip';
import { AIAccuracyPanel } from './AIAccuracyPanel';

const QUICK_CMDS = [
  { label: 'Today Squad', cmd: '/today_squad' },
  { label: 'Session Build', cmd: '/session_build' },
  { label: 'Match Prep', cmd: '/match_prep' },
];

export const MessageFeed = ({ user, onSubmit, onResumeSession }) => {
  const { state, setFeedElement } = useDecisionEngine();
  const { history, sessionId, recentRefreshKey } = state;

  return (
    <div
      ref={setFeedElement}
      className="flex-1 overflow-y-auto p-6 space-y-4"
      data-testid="decision-feed"
    >
      <RecentSessionsStrip
        refreshKey={recentRefreshKey}
        currentSessionId={sessionId}
        onResume={onResumeSession}
      />
      <AIAccuracyPanel user={user} />

      {history.length === 0 && (
        <div className="max-w-2xl mx-auto text-center py-12" data-testid="decision-engine-empty-state">
          <Sparkles className="h-10 w-10 mx-auto text-indigo-400 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Ask a question or use a command</h2>
          <p className="text-sm text-gray-500 mb-6">
            Type <code className="px-1 bg-gray-100 dark:bg-gray-800 rounded">/</code> to see options.
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {QUICK_CMDS.map((q) => (
              <Button
                key={q.cmd}
                variant="outline"
                size="sm"
                onClick={() => onSubmit(q.cmd)}
                data-testid={`quick-${q.cmd.slice(1)}`}
              >
                <ListChecks className="h-3.5 w-3.5 mr-1" />
                {q.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {history.map((turn) => (
        <div
          key={turn.id}
          className="max-w-3xl mx-auto space-y-3"
          data-testid={`turn-${turn.id}`}
        >
          {/* User query */}
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center text-xs font-semibold shrink-0">
              {user.full_name?.[0]?.toUpperCase() || 'C'}
            </div>
            <div className="flex-1 bg-gray-100 dark:bg-gray-900 rounded-lg px-4 py-2 text-sm">
              {turn.query}
            </div>
          </div>

          {/* Response / loading / error */}
          {turn.loading && (
            <div className="space-y-2">
              <RiskBanner loading />
              <Card>
                <CardContent className="py-4 flex items-center gap-3 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Reasoning…
                </CardContent>
              </Card>
            </div>
          )}

          {turn.error && (
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/30">
              <CardContent className="py-4 text-sm text-red-700 dark:text-red-400">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                {turn.error}
              </CardContent>
            </Card>
          )}

          {turn.response && (
            <div className="space-y-2">
              <RiskBanner flags={turn.response.pre_risk_flags} />
              <RiskFactorsStrip drivers={turn.response.pre_risk_flags?.drivers} />
              <ContextProvenanceBadge context={turn.context} />
              <RiskSnapshotInline snapshot={turn.risk_snapshot} evolution={turn.risk_evolution} />
              <DecisionCard response={turn.response} query={turn.query} sessionId={sessionId} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
